// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Offline Transaction Queue
// Implements IndexedDB-based queue with retry logic for NF525 compliance.
// CRITICAL MODULE: handles offline transactions for the POS.
// ---------------------------------------------------------------------------

import { apiClient } from '@/services/api-client';
import { useConfigStore } from '@/store/config-store';
import { useAuthStore } from '@/store/auth-store';
import { useCashStore } from '@/store/cash-store';
import {
  DB_NAME,
  DB_VERSION,
  OFFLINE_SYNC_INTERVAL,
  MAX_QUEUE_SIZE,
  RETRY_DELAYS,
} from '@/lib/constants';
import type { Transaction, Product } from '@/lib/types';

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface QueueItem {
  id?: number;
  transaction: Transaction;
  sequence: number;
  timestamp: string;
  status: 'pending' | 'failed';
  retry_count: number;
  last_attempt: string | null;
  error: string | null;
  device_id: string;
}

interface SyncedItem {
  id?: number;
  transaction_id: string | number;
  original_queue_id: number;
  transaction_data: Transaction;
  synced_at: string;
  server_response: Record<string, unknown>;
}

interface FiscalReceiptRecord {
  transaction_id: string | number;
  receipt: Record<string, unknown>;
  cached_at: string;
}

interface QueueStats {
  pending: number;
  failed: number;
  oldest: string | null;
  isSyncing: boolean;
  isOnline: boolean;
}

// ---------------------------------------------------------------------------
// Store names
// ---------------------------------------------------------------------------

const STORE_PENDING = 'pending_transactions';
const STORE_SYNCED = 'synced_transactions';
const STORE_PRODUCTS = 'product_cache';
const STORE_EVENTS = 'event_cache';
const STORE_METADATA = 'sync_metadata';
const STORE_FISCAL = 'fiscal_receipts';

// ---------------------------------------------------------------------------
// Offline Queue Manager
// ---------------------------------------------------------------------------

class OfflineQueueManager {
  private db: IDBDatabase | null = null;
  private isSyncing = false;
  private isPausedForAuth = false;
  private syncTimer: ReturnType<typeof setInterval> | null = null;
  private sequenceNumber = Date.now();
  private onlineHandler: (() => void) | null = null;

  // ---- initialization -----------------------------------------------------

  /**
   * Open IndexedDB and create the schema if needed.
   */
  async init(): Promise<void> {
    if (this.db) return;

    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineQueue] IndexedDB open error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[OfflineQueue] IndexedDB opened');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log(
          `[OfflineQueue] Upgrading IndexedDB from v${event.oldVersion} to v${event.newVersion}`,
        );

        // pending_transactions
        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          const store = db.createObjectStore(STORE_PENDING, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('sequence', 'sequence', { unique: true });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('retry_count', 'retry_count', { unique: false });
        }

        // synced_transactions
        if (!db.objectStoreNames.contains(STORE_SYNCED)) {
          const store = db.createObjectStore(STORE_SYNCED, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('transaction_id', 'transaction_id', { unique: false });
          store.createIndex('synced_at', 'synced_at', { unique: false });
        }

        // product_cache
        if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
          const store = db.createObjectStore(STORE_PRODUCTS, { keyPath: 'id' });
          store.createIndex('category', 'category', { unique: false });
          store.createIndex('updated_at', 'updated_at', { unique: false });
          store.createIndex('is_active', 'is_active', { unique: false });
        }

        // event_cache
        if (!db.objectStoreNames.contains(STORE_EVENTS)) {
          const store = db.createObjectStore(STORE_EVENTS, { keyPath: 'id' });
          store.createIndex('date', 'date', { unique: false });
          store.createIndex('is_active', 'is_active', { unique: false });
        }

        // sync_metadata
        if (!db.objectStoreNames.contains(STORE_METADATA)) {
          db.createObjectStore(STORE_METADATA, { keyPath: 'key' });
        }

        // fiscal_receipts
        if (!db.objectStoreNames.contains(STORE_FISCAL)) {
          const store = db.createObjectStore(STORE_FISCAL, {
            keyPath: 'transaction_id',
          });
          store.createIndex('cached_at', 'cached_at', { unique: false });
        }
      };
    });
  }

  // ---- enqueue ------------------------------------------------------------

  /**
   * Add a transaction to the offline queue.
   * Returns the auto-incremented queue ID.
   */
  async enqueueTransaction(transaction: Transaction): Promise<number> {
    await this.ensureDb();

    const sequence = this.sequenceNumber++;
    const { deviceId } = useConfigStore.getState();

    const queueItem: QueueItem = {
      transaction,
      sequence,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retry_count: 0,
      last_attempt: null,
      error: null,
      device_id: deviceId,
    };

    return new Promise<number>((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PENDING], 'readwrite');
      const store = tx.objectStore(STORE_PENDING);
      const request = store.add(queueItem);

      request.onsuccess = () => {
        const id = request.result as number;
        console.log(
          `[OfflineQueue] Transaction queued (ID: ${id}, Seq: ${sequence})`,
        );

        // Trigger immediate sync attempt
        this.triggerSync();
        resolve(id);
      };

      request.onerror = () => {
        console.error('[OfflineQueue] Failed to queue:', request.error);
        reject(request.error);
      };
    });
  }

  // ---- queries ------------------------------------------------------------

  /**
   * Get all pending transactions (status = 'pending').
   */
  async getPendingTransactions(): Promise<QueueItem[]> {
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PENDING], 'readonly');
      const store = tx.objectStore(STORE_PENDING);
      const index = store.index('status');
      const request = index.getAll('pending');

      request.onsuccess = () => resolve((request.result as QueueItem[]) ?? []);
      request.onerror = () => reject(request.error);
    });
  }

  // ---- sync ---------------------------------------------------------------

  /**
   * Attempt immediate sync (debounced).
   */
  triggerSync(): void {
    if (this.isSyncing) return;
    setTimeout(() => this.syncAll(), 500);
  }

  /**
   * Process all pending transactions sequentially.
   */
  async syncAll(): Promise<void> {
    if (this.isSyncing || !navigator.onLine || this.isPausedForAuth) return;

    this.isSyncing = true;
    console.log('[OfflineQueue] Starting sync...');

    try {
      const pending = await this.getPendingTransactions();
      if (pending.length === 0) {
        console.log('[OfflineQueue] No pending transactions');
        return;
      }

      console.log(`[OfflineQueue] Syncing ${pending.length} transactions`);

      let successCount = 0;
      let failCount = 0;

      for (const item of pending) {
        try {
          await this.syncTransaction(item);
          successCount++;
        } catch (error) {
          console.error(
            `[OfflineQueue] Sync failed for #${item.id}:`,
            (error as Error).message,
          );
          failCount++;
          await this.updateRetryStatus(
            item.id!,
            (error as Error).message,
          );
        }
      }

      console.log(
        `[OfflineQueue] Sync complete: ${successCount} success, ${failCount} failed`,
      );
    } catch (error) {
      console.error('[OfflineQueue] Sync error:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync a single transaction to the backend.
   */
  private async syncTransaction(queueItem: QueueItem): Promise<void> {
    const { id, transaction, sequence, retry_count, timestamp } = queueItem;
    const config = useConfigStore.getState();
    const auth = useAuthStore.getState();

    // Check auth before syncing — pause until re-login
    if (!auth.isAuthenticated()) {
      console.warn('[OfflineQueue] Token expired -- pausing sync until re-login');
      this.pauseForAuth();
      throw new Error('Token expire, reconnexion requise');
    }

    // Build unique transaction_id for deduplication
    const txId =
      transaction.transaction_id ||
      'caisse_' + timestamp.replace(/[^0-9]/g, '') + '_' + sequence;

    // NF525: attach active cash session
    const cashSessionId = useCashStore.getState().cashSessionId;

    const transactionWithMeta: Record<string, unknown> = {
      ...transaction,
      transaction_id: txId,
      source: 'caisse',
      agent_id: config.deviceId,
      company_id: config.companyId,
      pos_id: config.posId || null,
      employee_id: auth.user?.id,
      sequence_number: sequence,
      device_id: config.deviceId,
      offline_queued_at: timestamp,
      client_created_at: transaction.client_created_at || timestamp,
      cash_session_id: cashSessionId,
    };

    try {
      const response = await apiClient.createTransaction(transactionWithMeta);

      await this.markAsSynced(id!, response as Record<string, unknown>);

      // NF525: cache fiscal receipt (fire-and-forget)
      const serverId =
        (response as Record<string, unknown>).transaction_id ??
        (response as Record<string, unknown>).id;
      if (serverId) {
        this.fetchAndCacheReceipt(serverId as string | number).catch(() => {});
      }

      console.log(`[OfflineQueue] Transaction #${id} synced`);
    } catch (error) {
      // 401 = token expired
      if (
        (error as Error).message.includes('Session expiree') ||
        (error as Error).message.includes('401')
      ) {
        throw error;
      }

      // Max retries reached
      if (retry_count >= RETRY_DELAYS.length - 1) {
        await this.markAsFailed(id!, (error as Error).message);
        throw new Error('Max retries exceeded');
      }

      throw error;
    }
  }

  // ---- mark as synced / failed --------------------------------------------

  private async markAsSynced(
    queueId: number,
    serverResponse: Record<string, unknown>,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(
        [STORE_PENDING, STORE_SYNCED],
        'readwrite',
      );
      const pendingStore = tx.objectStore(STORE_PENDING);
      const syncedStore = tx.objectStore(STORE_SYNCED);

      const getRequest = pendingStore.get(queueId);

      getRequest.onsuccess = () => {
        const item = getRequest.result as QueueItem | undefined;
        if (!item) {
          reject(new Error('Queue item not found'));
          return;
        }

        syncedStore.add({
          transaction_id:
            serverResponse.transaction_id ?? item.sequence,
          original_queue_id: queueId,
          transaction_data: item.transaction,
          synced_at: new Date().toISOString(),
          server_response: serverResponse,
        } as SyncedItem);

        pendingStore.delete(queueId);
        resolve();
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async markAsFailed(queueId: number, errorMessage: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PENDING], 'readwrite');
      const store = tx.objectStore(STORE_PENDING);
      const getRequest = store.get(queueId);

      getRequest.onsuccess = () => {
        const item = getRequest.result as QueueItem | undefined;
        if (!item) {
          reject(new Error('Queue item not found'));
          return;
        }

        item.status = 'failed';
        item.error = errorMessage;

        const updateRequest = store.put(item);
        updateRequest.onsuccess = () => {
          console.error(
            `[OfflineQueue] Transaction #${queueId} permanently failed: ${errorMessage}`,
          );
          resolve();
        };
        updateRequest.onerror = () => reject(updateRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  private async updateRetryStatus(
    queueId: number,
    errorMessage: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PENDING], 'readwrite');
      const store = tx.objectStore(STORE_PENDING);
      const getRequest = store.get(queueId);

      getRequest.onsuccess = () => {
        const item = getRequest.result as QueueItem | undefined;
        if (!item) {
          reject(new Error('Queue item not found'));
          return;
        }

        item.retry_count++;
        item.last_attempt = new Date().toISOString();
        item.error = errorMessage;

        // If max retries exceeded, mark as failed
        if (item.retry_count >= RETRY_DELAYS.length) {
          item.status = 'failed';
        }

        const updateRequest = store.put(item);
        updateRequest.onsuccess = () => {
          console.warn(
            `[OfflineQueue] Transaction #${queueId} retry count: ${item.retry_count}`,
          );
          resolve();
        };
        updateRequest.onerror = () => reject(updateRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ---- product cache ------------------------------------------------------

  /**
   * Cache products in IndexedDB (clear + refill).
   */
  async cacheProducts(products: Product[]): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PRODUCTS], 'readwrite');
      const store = tx.objectStore(STORE_PRODUCTS);

      store.clear();

      for (const product of products) {
        store.put({
          ...product,
          updated_at: new Date().toISOString(),
        });
      }

      tx.oncomplete = () => {
        console.log(`[OfflineQueue] Cached ${products.length} products`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Retrieve cached products (offline fallback).
   */
  async getCachedProducts(): Promise<Product[]> {
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PRODUCTS], 'readonly');
      const store = tx.objectStore(STORE_PRODUCTS);
      const request = store.getAll();

      request.onsuccess = () => {
        const products = (request.result as Product[]) ?? [];
        console.log(`[OfflineQueue] Retrieved ${products.length} cached products`);
        resolve(products);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ---- fiscal receipts cache (NF525) --------------------------------------

  /**
   * Cache a fiscal receipt for offline viewing.
   */
  async cacheFiscalReceipt(
    transactionId: string | number,
    receiptData: Record<string, unknown>,
  ): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_FISCAL], 'readwrite');
      const store = tx.objectStore(STORE_FISCAL);

      store.put({
        transaction_id: transactionId,
        receipt: receiptData,
        cached_at: new Date().toISOString(),
      } as FiscalReceiptRecord);

      tx.oncomplete = () => {
        console.log(`[NF525] Receipt #${transactionId} cached`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Retrieve a cached fiscal receipt.
   */
  async getCachedFiscalReceipt(
    transactionId: string | number,
  ): Promise<Record<string, unknown> | null> {
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_FISCAL], 'readonly');
      const store = tx.objectStore(STORE_FISCAL);
      const request = store.get(transactionId);

      request.onsuccess = () => {
        const result = request.result as FiscalReceiptRecord | undefined;
        if (result) {
          console.log(`[NF525] Receipt #${transactionId} loaded from cache`);
          resolve(result.receipt);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Fetch and cache a fiscal receipt after a successful sync.
   * Fire-and-forget, non-blocking.
   */
  private async fetchAndCacheReceipt(
    serverTransactionId: string | number,
  ): Promise<void> {
    if (!serverTransactionId) return;

    try {
      const receiptData = await apiClient.getReceipt(serverTransactionId);
      await this.cacheFiscalReceipt(
        serverTransactionId,
        receiptData as Record<string, unknown>,
      );
    } catch (error) {
      console.warn(
        `[NF525] Post-sync receipt cache failed for #${serverTransactionId}:`,
        (error as Error).message,
      );
    }
  }

  // ---- statistics ---------------------------------------------------------

  /**
   * Get queue statistics.
   */
  async getStats(): Promise<QueueStats> {
    if (!this.db) {
      return {
        pending: 0,
        failed: 0,
        oldest: null,
        isSyncing: this.isSyncing,
        isOnline: navigator.onLine,
      };
    }

    const allPending = await this.getAllItems();
    const pending = allPending.filter((t) => t.status === 'pending');
    const failed = allPending.filter((t) => t.status === 'failed');

    return {
      pending: pending.length,
      failed: failed.length,
      oldest: pending[0]?.timestamp ?? null,
      isSyncing: this.isSyncing,
      isOnline: navigator.onLine,
    };
  }

  private async getAllItems(): Promise<QueueItem[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_PENDING], 'readonly');
      const store = tx.objectStore(STORE_PENDING);
      const request = store.getAll();

      request.onsuccess = () => resolve((request.result as QueueItem[]) ?? []);
      request.onerror = () => reject(request.error);
    });
  }

  // ---- background sync control --------------------------------------------

  /**
   * Start periodic background sync and listen for online events.
   */
  startBackgroundSync(): void {
    this.stopBackgroundSync();

    // Immediate sync
    this.syncAll().catch(() => {});

    // Periodic sync
    this.syncTimer = setInterval(() => {
      this.syncAll().catch(() => {});
    }, OFFLINE_SYNC_INTERVAL);

    // Sync when coming back online
    this.onlineHandler = () => {
      console.log('[OfflineQueue] Network online -- triggering sync');
      this.syncAll().catch(() => {});
    };
    window.addEventListener('online', this.onlineHandler);
  }

  /**
   * Stop periodic background sync.
   */
  stopBackgroundSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
  }

  /** Alias for stopBackgroundSync. */
  stop(): void {
    this.stopBackgroundSync();
    console.log('[OfflineQueue] Background sync stopped');
  }

  // ---- cleanup ------------------------------------------------------------

  /**
   * Delete old synced transactions (retention policy).
   * @param daysToKeep - Number of days to keep (default 30).
   */
  async cleanupHistory(daysToKeep = 30): Promise<number> {
    if (!this.db) return 0;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysToKeep);
    const cutoffStr = cutoff.toISOString();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_SYNCED], 'readwrite');
      const store = tx.objectStore(STORE_SYNCED);
      const index = store.index('synced_at');
      const range = IDBKeyRange.upperBound(cutoffStr);
      const request = index.openCursor(range);

      let deleted = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          deleted++;
          cursor.continue();
        } else {
          console.log(`[OfflineQueue] Cleaned up ${deleted} old synced transactions`);
          resolve(deleted);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete old fiscal receipts cache.
   * NF525: long-term archival is server-side; local cache is 90 days by default.
   *
   * @param daysToKeep - Number of days to keep (default 90).
   */
  async cleanupFiscalReceipts(daysToKeep = 90): Promise<number> {
    if (!this.db) return 0;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysToKeep);
    const cutoffStr = cutoff.toISOString();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_FISCAL], 'readwrite');
      const store = tx.objectStore(STORE_FISCAL);
      const index = store.index('cached_at');
      const range = IDBKeyRange.upperBound(cutoffStr);
      const request = index.openCursor(range);

      let deleted = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          deleted++;
          cursor.continue();
        } else {
          if (deleted > 0) {
            console.log(
              `[NF525] Cleaned up ${deleted} fiscal receipts > ${daysToKeep}d`,
            );
          }
          resolve(deleted);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ---- auth pause / resume ------------------------------------------------

  /**
   * Pause background sync when auth token expires (401).
   * Sync will remain paused until `resumeAfterAuth()` is called.
   * This prevents wasted retries and potential rate-limiting.
   */
  private pauseForAuth(): void {
    if (this.isPausedForAuth) return;
    this.isPausedForAuth = true;
    console.warn('[OfflineQueue] Sync paused — waiting for re-authentication');

    // Listen for Zustand auth store changes to auto-resume
    const unsub = useAuthStore.subscribe((state) => {
      if (state._isAuthenticated) {
        this.resumeAfterAuth();
        unsub();
      }
    });
  }

  /**
   * Resume background sync after successful re-authentication.
   */
  resumeAfterAuth(): void {
    if (!this.isPausedForAuth) return;
    this.isPausedForAuth = false;
    console.log('[OfflineQueue] Auth restored — resuming sync');
    this.triggerSync();
  }

  // ---- internal helpers ---------------------------------------------------

  private async ensureDb(): Promise<void> {
    if (!this.db) await this.init();
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const offlineQueue = new OfflineQueueManager();
